import * as React from 'react';
import { useLocation } from 'react-router-dom';

type HistoryState = {
  stack: string[];
  index: number;
};

type HistoryNavigationContextValue = {
  state: HistoryState;
};

const HistoryNavigationContext = React.createContext<HistoryNavigationContextValue | null>(null);

const getLocationKey = (location: ReturnType<typeof useLocation>): string | null => {
  if (location.key) {
    return location.key;
  }

  if (location.pathname) {
    return `${location.pathname}${location.search}${location.hash}`;
  }

  return null;
};

type HistoryNavigationProviderProps = {
  children: React.ReactNode;
};

export const HistoryNavigationProvider: React.FC<HistoryNavigationProviderProps> = ({ children }) => {
  const location = useLocation();
  const locationKey = getLocationKey(location);

  const [state, setState] = React.useState<HistoryState>(() => ({
    stack: locationKey ? [locationKey] : [],
    index: 0,
  }));

  React.useEffect(() => {
    if (!locationKey) {
      return;
    }

    setState((prev) => {
      const existingIndex = prev.stack.indexOf(locationKey);

      if (existingIndex === -1) {
        const nextStack = prev.stack.slice(0, prev.index + 1);

        nextStack.push(locationKey);

        return {
          stack: nextStack,
          index: nextStack.length - 1,
        };
      }

      if (existingIndex === prev.index) {
        return prev;
      }

      return {
        stack: prev.stack,
        index: existingIndex,
      };
    });
  }, [locationKey]);

  const value = React.useMemo<HistoryNavigationContextValue>(() => ({ state }), [state]);

  return <HistoryNavigationContext.Provider value={value}>{children}</HistoryNavigationContext.Provider>;
};

const useHistoryNavigationState = () => {
  const context = React.useContext(HistoryNavigationContext);

  if (!context) {
    throw new Error('useHistoryNavigationState must be used within HistoryNavigationProvider');
  }

  return context.state;
};

export default useHistoryNavigationState;
